"use client";

import Image from "@/components/CwfImage";
import { usePathname, useRouter } from "@/lib/cwfNavigation";
import { Link, Outlet } from "react-router-dom";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/lib/api";
import {
  fetchLiveEvents,
  LIVE_EVENTS_STALE_MS,
  liveEventsQueryKey,
} from "@/lib/events/liveEventsQuery";
import { useSession } from "@/stores/session";
import { useEnergyMeta, tickEnergyRegen } from "@/stores/energyMeta";
import { CurrencyIcon } from "@/components/ui/CurrencyIcons";
import { AudioSettingsModal } from "@/components/AudioSettingsModal";
import { BottomNav } from "@/components/BottomNav";
import { NoEnergyModal } from "@/components/NoEnergyModal";
import { playDooberFly, playDooberLand, playUiTap } from "@/lib/audio/play";
import { PurchaseCelebrationLayer } from "@/components/celebration/PurchaseCelebrationLayer";
import {
  buildSpecsFromPendingFlies,
  EventIconFlyLayer,
  type EventIconFlySpec,
} from "@/components/EventIconFlyLayer";
import {
  consumePendingCoinFlyAmount,
  CWF_COIN_FLY_HUD_EVENT,
} from "@/lib/coinFlyToHud";
import type { PendingEventIconFly } from "@/lib/eventIconFlyToMenu";
import { consumePendingEventIconFlies } from "@/lib/eventIconFlyToMenu";
import { EventTierRewardModal } from "@/components/events/EventTierRewardModal";
import { FloatingSpadesLayer } from "@/components/shell/FloatingSpadesLayer";

function formatN(n: number): string {
  return n >= 10000 ? `${Math.round(n / 1000)}k` : n.toLocaleString();
}

const PLUS_BTN_CLASS =
  "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 border-[#1d4e9e] bg-gradient-to-b from-[#d4f0ff] via-[#5eb5ff] to-[#1e84eb] text-[0.72rem] font-black leading-none text-white shadow-[inset_0_2px_0_rgba(255,255,255,0.55),inset_0_-2px_0_rgba(0,45,120,0.25),0_2px_0_#123f7a,0_3px_6px_rgba(0,25,80,0.35)] transition hover:brightness-105 active:translate-y-px sm:h-7 sm:w-7 sm:rounded-lg sm:text-[0.82rem]";

/** All top-HUD resource values (coin / gem / energy) share this size; `md:` tames desktop caps. */
const HUD_RESOURCE_VALUE_CLASS =
  "text-[clamp(0.9rem,3.2vw,1.55rem)] md:text-[clamp(0.82rem,1.45vw,1.22rem)]";

/** Light blue glossy + on coin/gem/energy HUD pills — `href` uses prefetched client navigation (snappier than deferred router.push). */
function CurrencyPlusButton({
  href,
  onClick,
  label = "Get more",
  onWarmNavigate,
}: {
  href?: string;
  onClick?: () => void;
  label?: string;
  onWarmNavigate?: () => void;
}) {
  const warm = {
    onPointerEnter: onWarmNavigate,
    onPointerDown: onWarmNavigate,
    onFocus: onWarmNavigate,
  };
  const glyph = (
    <span className="inline-block origin-center scale-[1.65] drop-shadow-[0_1px_0_rgba(12,40,90,0.45)] sm:scale-[1.85]">
      +
    </span>
  );
  if (href) {
    return (
      <Link
        to={href}
        aria-label={label}
        className={PLUS_BTN_CLASS}
        {...warm}
        onClick={() => {
          try {
            playUiTap();
          } catch {
            /* noop */
          }
        }}
      >
        {glyph}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        try {
          playUiTap();
        } catch {
          /* noop */
        }
        onClick?.();
      }}
      aria-label={label}
      className={PLUS_BTN_CLASS}
      {...warm}
    >
      {glyph}
    </button>
  );
}

/** Coin/gem/energy — no bezel; modestly oversized vs slim pill (still peeks past top/bottom). */
const HUD_CURRENCY_ART_DIM =
  "h-[4.5rem] w-[4.5rem] max-[380px]:h-16 max-[380px]:w-16 sm:h-20 sm:w-20";
const HUD_CURRENCY_OVERFLOW_ART =
  `relative z-[2] shrink-0 object-contain pointer-events-none ${HUD_CURRENCY_ART_DIM}`;
const HUD_ART_SHADOW_COIN =
  "drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]";
const HUD_ART_SHADOW_GEM =
  "drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]";
const HUD_ART_SHADOW_ENERGY =
  "drop-shadow-[0_2px_7px_rgba(2,48,71,0.55)]";

/** Gold coin — bare PNG; sits in front of the bar with light overflow. */
function CoinIcon() {
  return (
    <span
      className={`relative flex shrink-0 items-center justify-center overflow-visible ${HUD_CURRENCY_ART_DIM}`}
      aria-hidden
    >
      <CurrencyIcon
        kind="coin"
        size={80}
        className={`${HUD_CURRENCY_OVERFLOW_ART} ${HUD_ART_SHADOW_COIN}`}
      />
    </span>
  );
}

/** Gem — bare PNG; sits in front of the bar with light overflow. */
function GemIcon() {
  return (
    <span
      className={`relative flex shrink-0 items-center justify-center overflow-visible ${HUD_CURRENCY_ART_DIM}`}
      aria-hidden
    >
      <CurrencyIcon
        kind="gem"
        size={80}
        className={`${HUD_CURRENCY_OVERFLOW_ART} ${HUD_ART_SHADOW_GEM}`}
      />
    </span>
  );
}

/** Lightning — bare PNG like coin/gem; overlaps slim pill. */
function EnergyIcon() {
  return (
    <span
      className={`relative flex shrink-0 items-center justify-center overflow-visible ${HUD_CURRENCY_ART_DIM}`}
      aria-hidden
    >
      <CurrencyIcon
        kind="energy"
        size={80}
        className={`${HUD_CURRENCY_OVERFLOW_ART} ${HUD_ART_SHADOW_ENERGY}`}
      />
    </span>
  );
}

/** Gold coin for reward fly VFX (~2× HUD chip so reads clearly on arrival) */
function FlyCoinSprite() {
  return (
    <div
      className="flex h-[1.9rem] w-[1.9rem] shrink-0 items-center justify-center rounded-full border-2 border-[#b45309] bg-gradient-to-br from-[#fde047] via-[#eab308] to-[#ca8a04] shadow-[inset_0_2px_0_rgba(255,255,255,0.45),0_4px_14px_rgba(0,0,0,0.4)]"
      aria-hidden
    >
      <CurrencyIcon kind="coin" size={22} className="h-[1.2rem] w-[1.2rem]" />
    </div>
  );
}

type CoinFlySpec = {
  id: string;
  sx: number;
  sy: number;
  dx: number;
  dy: number;
  delay: number;
};

function quadBez1d(t: number, p0: number, p1: number, p2: number): number {
  const u = 1 - t;
  return u * u * p0 + 2 * u * t * p1 + t * t * p2;
}

/** Stable 0–1 hash for per-doober arc variation (no Math.random in effects). */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10001) / 10000;
}

/** Quadratic arc + landing “plump” into the HUD coin slot. */
function buildCoinFlyKeyframes(spec: CoinFlySpec): Keyframe[] {
  const { dx, dy, id } = spec;
  const r1 = hash01(`${id}:arc`);
  const r2 = hash01(`${id}:sway`);

  const arcLift = 100 + r1 * 85;
  const sway = (r2 - 0.5) * 96;
  const p1x = dx * 0.5 + sway;
  const p1y = dy * 0.32 - arcLift;

  const steps = [0, 0.1, 0.22, 0.36, 0.5, 0.62, 0.72, 0.79, 0.84, 0.87, 0.9, 0.935, 1];

  return steps.map((t) => {
    const x = quadBez1d(t, 0, p1x, dx);
    const y = quadBez1d(t, 0, p1y, dy);
    let sc: number;
    let rot: number;

    if (t < 0.72) {
      const u = t / 0.72;
      const swell = Math.sin(u * Math.PI);
      sc = 1.03 + swell * 0.16;
      rot = (1 - u) * -22 + u * -4;
    } else if (t < 0.815) {
      const u = (t - 0.72) / 0.095;
      const start = 1.03;
      sc = start - u * (start - 0.46);
      rot = -4 * (1 - u);
    } else if (t < 0.865) {
      sc = 0.46;
      rot = 0;
    } else if (t < 0.895) {
      const u = (t - 0.865) / 0.03;
      sc = 0.46 + u * (1.24 - 0.46);
      rot = 0;
    } else if (t < 0.935) {
      const u = (t - 0.895) / 0.04;
      sc = 1.24 - u * (1.24 - 0.36);
      rot = 0;
    } else {
      const u = (t - 0.935) / 0.065;
      sc = 0.36 - u * 0.22;
      rot = 0;
    }

    return {
      transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) scale(${sc.toFixed(3)}) rotate(${rot.toFixed(2)}deg)`,
      offset: t,
    };
  });
}

function CoinFlyToHudLayer(props: {
  specs: CoinFlySpec[];
  onAllFinished: () => void;
}) {
  const { specs, onAllFinished } = props;
  const refMap = useRef(new Map<string, HTMLDivElement | null>());
  const onDoneRef = useRef(onAllFinished);
  onDoneRef.current = onAllFinished;

  useLayoutEffect(() => {
    if (specs.length === 0) return;
    try {
      playDooberFly();
    } catch {
      /* noop */
    }
    const anims: Animation[] = [];
    let finished = 0;
    const bump = () => {
      finished += 1;
      if (finished >= specs.length) {
        onDoneRef.current();
      }
    };
    for (const spec of specs) {
      const el = refMap.current.get(spec.id);
      if (!el) {
        bump();
        continue;
      }
      const anim = el.animate(buildCoinFlyKeyframes(spec), {
        duration: 840,
        delay: spec.delay,
        easing: "cubic-bezier(0.22, 0.04, 0.2, 1)",
        fill: "forwards",
      });
      anims.push(anim);
      anim.onfinish = () => bump();
    }
    return () => {
      for (const a of anims) {
        try {
          a.cancel();
        } catch {
          /* noop */
        }
      }
    };
  }, [specs]);

  return (
    <>
      {specs.map((spec) => (
        <div
          key={spec.id}
          className="pointer-events-none fixed z-[124]"
          style={{
            left: `${spec.sx}px`,
            top: `${spec.sy}px`,
            transform: "translate(-50%, -50%)",
          }}
          aria-hidden
        >
          <div
            ref={(el) => {
              refMap.current.set(spec.id, el);
            }}
            className="relative will-change-transform"
          >
            <FlyCoinSprite />
          </div>
        </div>
      ))}
    </>
  );
}

function CurrencyPill({
  icon,
  value,
  onPlus,
  plusHref,
  plusLabel = "Get more",
  onPlusWarmNavigate,
  className,
  /** Rare override; default is the shared HUD resource size (`HUD_RESOURCE_VALUE_CLASS`). */
  valueClassName,
  /** `center` matches all three HUD chips; use `start` only if needed. */
  valueAlign = "center",
  /** 0–100: orange gradient fill inside the pill (energy HUD). Omit for coin/gem. */
  progressFillPct,
  /** Energy `current/max` must stay visible on narrow phones — no ellipsis. */
  valueNoTruncate = false,
}: {
  icon: ReactNode;
  value: string;
  onPlus?: () => void;
  /** Store shortcut — Next `<Link>` + prefetch for faster hops than imperative push. */
  plusHref?: string;
  plusLabel?: string;
  onPlusWarmNavigate?: () => void;
  className?: string;
  valueClassName?: string;
  valueAlign?: "start" | "center";
  progressFillPct?: number;
  valueNoTruncate?: boolean;
}) {
  const fillPct =
    typeof progressFillPct === "number"
      ? Math.min(100, Math.max(0, progressFillPct))
      : null;

  const sizeClass = valueClassName ?? HUD_RESOURCE_VALUE_CLASS;
  const alignIsCenter = valueAlign === "center";

  return (
    <div
      className={`relative isolate flex min-h-[40px] min-w-0 flex-1 items-center overflow-visible sm:min-h-[44px] ${className ?? ""}`}
    >
      {/* Coin/gem/energy art sits above the pill strip */}
      <div className="pointer-events-none absolute left-[34px] top-1/2 z-[5] -translate-x-1/2 -translate-y-1/2 max-[380px]:left-[30px] sm:left-[38px]">
        {icon}
      </div>
      {/* Slim pill — energy: SPIN-style orange gradient + darken unfilled from right; coin/gem: glass strip */}
      <div
        className={[
          "relative ml-[22px] flex min-h-[26px] min-w-0 flex-1 items-center gap-0.5 rounded-[12px] py-px max-[380px]:min-h-[24px] max-[380px]:gap-0.5 max-[380px]:pl-[24px] max-[380px]:pr-0.5 sm:ml-[24px] sm:min-h-[30px] sm:gap-1.5 sm:rounded-[14px] sm:pl-[54px] sm:pr-1.5",
          fillPct != null
            ? "overflow-visible border border-white/90 bg-gradient-to-b from-[#FFD34E] to-[#E88B01] pl-[28px] pr-0.5 shadow-[inset_0_2px_0_rgba(255,255,255,0.38)]"
            : "gap-1 overflow-hidden pl-[30px] pr-[5px]",
        ].join(" ")}
      >
        {fillPct != null ? (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-0 overflow-hidden rounded-r-[12px] sm:rounded-r-[14px]"
            style={{ width: `${Math.min(100, Math.max(0, 100 - fillPct))}%` }}
            aria-hidden
          >
            <div className="h-full w-full bg-gradient-to-b from-black/30 to-black/48" />
          </div>
        ) : null}
        <p
          className={[
            "relative z-[1] min-w-0 flex-1 py-px font-display font-extrabold tabular-nums leading-none tracking-tight text-white drop-shadow-[1px_2px_0_rgba(0,0,0,0.55)] sm:py-px",
            alignIsCenter
              ? "flex items-center justify-center text-center"
              : "text-left",
            sizeClass,
          ].join(" ")}
        >
          <span
            className={
              valueNoTruncate
                ? "whitespace-nowrap"
                : "min-w-0 max-w-full truncate"
            }
          >
            {value}
          </span>
        </p>
        <div className="relative z-[2] shrink-0">
          <CurrencyPlusButton
            href={plusHref}
            onClick={plusHref ? undefined : onPlus}
            label={plusLabel}
            onWarmNavigate={onPlusWarmNavigate}
          />
        </div>
      </div>
    </div>
  );
}

function EnergyBlock({
  current,
  max,
  onPlusWarmNavigate,
}: {
  current: number;
  max: number;
  /** Extra prefetch on hover/tap-start (route also prefetched when logged in). */
  onPlusWarmNavigate?: () => void;
}) {
  const value = `${current}/${max}`;
  const pct = Math.min(100, Math.round((current / Math.max(max, 1)) * 100));
  return (
    <CurrencyPill
      icon={<EnergyIcon />}
      value={value}
      plusHref="/store?tab=energy"
      plusLabel="Refill energy"
      onPlusWarmNavigate={onPlusWarmNavigate}
      progressFillPct={pct}
      valueNoTruncate
      valueClassName="text-[clamp(0.62rem,2vw,0.95rem)] md:text-[clamp(0.82rem,1.45vw,1.22rem)]"
    />
  );
}

/** Vector settings cog — always renders; optional PNG via `npm run generate:settings-icon`. */
function SettingsGearIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81a.48.48 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41L9.25 5.35c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </svg>
  );
}

function SettingsButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        try {
          playUiTap();
        } catch {
          /* noop */
        }
        onClick();
      }}
      className="flex h-[1.5rem] w-[1.5rem] shrink-0 items-center justify-center overflow-hidden rounded-md border border-black/35 bg-black/25 text-white/95 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition hover:brightness-110 active:scale-95 sm:h-[1.75rem] sm:w-[1.75rem]"
      aria-label="Audio & settings"
    >
      <SettingsGearIcon className="h-[1.05rem] w-[1.05rem] shrink-0 sm:h-[1.2rem] sm:w-[1.2rem]" />
    </button>
  );
}

export function AppShell({
  children,
  hideTopHud = false,
}: {
  children?: ReactNode;
  /** Hide currency / energy / settings strip (immersive overlays e.g. category reveal). */
  hideTopHud?: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [coinFlySpecs, setCoinFlySpecs] = useState<CoinFlySpec[]>([]);
  const [eventIconFlySpecs, setEventIconFlySpecs] = useState<EventIconFlySpec[]>([]);
  const [hudCoinPop, setHudCoinPop] = useState(false);
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  const [tierRewardGate, setTierRewardGate] = useState<null | {
    flies: PendingEventIconFly[];
    coins: number;
  }>(null);

  const handleEventIconFlyDone = useCallback(() => {
    try {
      playDooberLand();
    } catch {
      /* noop */
    }
    setEventIconFlySpecs([]);
  }, []);

  const handleCoinFlyDone = useCallback(() => {
    try {
      playDooberLand();
    } catch {
      /* noop */
    }
    setCoinFlySpecs([]);
    setHudCoinPop(true);
    window.setTimeout(() => setHudCoinPop(false), 620);
  }, []);

  const triggerCoinFlyToHud = useCallback((amount: number) => {
    const n0 = Math.max(0, Math.round(amount));
    if (n0 <= 0) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setHudCoinPop(true);
      window.setTimeout(() => setHudCoinPop(false), 450);
      return;
    }

    const run = () => {
      const target = document.getElementById("cwf-hud-coin-target");
      if (!target) {
        setHudCoinPop(true);
        window.setTimeout(() => setHudCoinPop(false), 450);
        return;
      }
      const rect = target.getBoundingClientRect();
      const tcx = rect.left + rect.width / 2;
      const tcy = rect.top + rect.height / 2;
      const n = Math.min(16, Math.max(6, Math.floor(n0 / 90)));
      const next: CoinFlySpec[] = Array.from({ length: n }, (_, i) => {
        const t = n <= 1 ? 0.5 : i / (n - 1);
        const angle = t * Math.PI * 1.72 + (Math.random() - 0.5) * 0.85;
        const radius = 95 + Math.random() * 105;
        const sx = window.innerWidth * 0.5 + Math.cos(angle) * radius * 0.52;
        const sy = window.innerHeight * 0.54 + Math.sin(angle) * 48 + (Math.random() - 0.5) * 36;
        const dx = tcx - sx;
        const dy = tcy - sy;
        return {
          id: `fly-${i}-${Math.random().toString(36).slice(2, 10)}`,
          sx,
          sy,
          dx,
          dy,
          delay: i * 36,
        };
      });
      setCoinFlySpecs(next);
    };

    requestAnimationFrame(run);
  }, []);

  const finishDeferredTierRewards = useCallback(
    (p: { flies: PendingEventIconFly[]; coins: number }) => {
      const rm =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (p.coins > 0) {
        triggerCoinFlyToHud(p.coins);
      }
      if (p.flies.length > 0 && !rm) {
        window.requestAnimationFrame(() => {
          setEventIconFlySpecs(buildSpecsFromPendingFlies(p.flies));
        });
      }
    },
    [triggerCoinFlyToHud],
  );

  useLayoutEffect(() => {
    if (pathname !== "/") {
      setCoinFlySpecs([]);
      setEventIconFlySpecs([]);
      return;
    }
    const pending = consumePendingEventIconFlies();
    const coinQueued = consumePendingCoinFlyAmount();
    const gated =
      pending.length > 0 &&
      pending.some((x) => x.completed && x.bonusCoins > 0);

    if (gated) {
      const milestoneCoinSum = pending.reduce(
        (s, x) =>
          x.completed && x.bonusCoins > 0 ? s + x.bonusCoins : s,
        0,
      );
      setTierRewardGate({
        flies: pending,
        coins: Math.max(coinQueued, milestoneCoinSum),
      });
      return;
    }

    if (coinQueued > 0) {
      triggerCoinFlyToHud(coinQueued);
    }

    let rafId = 0;
    if (
      pending.length > 0 &&
      typeof window !== "undefined" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      rafId = window.requestAnimationFrame(() => {
        setEventIconFlySpecs(buildSpecsFromPendingFlies(pending));
      });
    }
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [pathname, triggerCoinFlyToHud]);

  /** If player leaves home before acknowledging the gated modal, flush VFX anyway. */
  useEffect(() => {
    if (pathname === "/" || tierRewardGate === null) return;
    const snapshot = tierRewardGate;
    setTierRewardGate(null);
    finishDeferredTierRewards(snapshot);
  }, [pathname, tierRewardGate, finishDeferredTierRewards]);

  useEffect(() => {
    function onCoinFlyHud(ev: Event) {
      const d = (ev as CustomEvent<{ amount?: number }>).detail;
      const amount =
        typeof d?.amount === "number" && Number.isFinite(d.amount)
          ? Math.max(0, Math.round(d.amount))
          : 0;
      triggerCoinFlyToHud(amount);
    }
    window.addEventListener(CWF_COIN_FLY_HUD_EVENT, onCoinFlyHud);
    return () => window.removeEventListener(CWF_COIN_FLY_HUD_EVENT, onCoinFlyHud);
  }, [triggerCoinFlyToHud]);

  const hideDock =
    pathname === "/play" || pathname === "/matchmaking";
  const matchmakingPath = pathname === "/matchmaking";
  const playDuelPath = pathname === "/play";
  const playScene = hideDock;
  /** Full-bleed raster menu backdrop (Quiz Quest–style pale radial sky). */
  const homeMenuPhotoBg = pathname === "/" && !playScene;
  /** Profile hub: same sky plate as home, softened so the card reads clearly. */
  const profileRasterBg = pathname === "/profile";
  const photoMenuBackdrop = homeMenuPhotoBg || profileRasterBg;
  /** Desktop/tablet: these hubs span full shell width (main not max-w-xl). */
  const hubDesktopFullBleed =
    pathname === "/events" ||
    pathname === "/leaderboard" ||
    pathname === "/friends" ||
    pathname === "/store";
  /** Home menu: no scroll panes; logo may overflow upward past main. */
  const homeStaticShell = pathname === "/" && !hideDock;
  const { token } = useSession();

  const me = useQuery({
    enabled: !!token,
    queryKey: ["me", token],
    queryFn: () =>
      apiJson<{
        displayName: string;
        chefLevel: number;
        coins: number;
        gems: number;
      }>("/me", { token }),
    staleTime: 60_000,
  });

  const coins = me.data?.coins ?? 0;
  const gems = me.data?.gems ?? 0;

  const energyLive = useEnergyMeta((s) => s.energy);
  const energyMaxStored = useEnergyMeta((s) => s.energyMax);

  useEffect(() => {
    tickEnergyRegen();
    const id = window.setInterval(() => tickEnergyRegen(), 20_000);
    return () => clearInterval(id);
  }, []);

  const energyCur = energyLive;
  const energyMax = energyMaxStored;

  useEffect(() => {
    if (!token) return;
    void queryClient.prefetchQuery({
      queryKey: liveEventsQueryKey(token),
      queryFn: () => fetchLiveEvents(token),
      staleTime: LIVE_EVENTS_STALE_MS,
    });
    void router.prefetch("/matchmaking");
    void router.prefetch("/profile");
    void router.prefetch("/events");
    void router.prefetch("/friends");
    void router.prefetch("/store");
    void router.prefetch("/store?tab=coins");
    void router.prefetch("/store?tab=gems");
    void router.prefetch("/store?tab=energy");
  }, [token, router, queryClient]);

  return (
    <div
      className={`relative isolate flex flex-col ${
        hideDock
          ? matchmakingPath || playDuelPath
            ? "h-[100dvh] max-h-[100dvh] overflow-hidden"
            : "min-h-screen"
          : homeStaticShell
            /** `overflow-x-hidden` + `visible` y clips due to CSS rule — need full visible for logo bleed */
            ? "h-[100dvh] max-h-[100dvh] overflow-visible"
            : "h-[100dvh] max-h-[100dvh] overflow-hidden"
      }`}
    >
      {photoMenuBackdrop ? (
        <div
          className="pointer-events-none fixed inset-0 -z-20 overflow-hidden"
          aria-hidden
        >
          <Image
            src="/bg32.webp"
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover object-[center_32%]"
          />
        </div>
      ) : (
        <div
          className={`pointer-events-none fixed inset-0 -z-20 ${
            playScene ? "cwf-shell-backdrop--duel" : "cwf-shell-backdrop"
          }`}
          aria-hidden
        />
      )}
      <FloatingSpadesLayer duel={playScene} />
      {!playScene ? (
        <>
          <div
            className={
              photoMenuBackdrop
                ? "pointer-events-none fixed inset-0 -z-10 bg-gradient-to-b from-white/16 via-sky-50/12 to-sky-200/22"
                : "pointer-events-none fixed inset-0 -z-10 bg-gradient-to-b from-white/35 via-sky-100/25 to-sky-300/35"
            }
            aria-hidden
          />
          <div
            className={
              photoMenuBackdrop
                ? "pointer-events-none fixed inset-0 -z-10 bg-gradient-to-t from-sky-900/12 via-transparent to-white/28"
                : "pointer-events-none fixed inset-0 -z-10 bg-gradient-to-t from-sky-800/18 via-transparent to-white/45"
            }
            aria-hidden
          />
        </>
      ) : null}

      {!hideTopHud ? (
        <header className="sticky top-0 z-40 overflow-visible border-b border-black/35 bg-[#2563cf] pt-[max(0px,env(safe-area-inset-top))] shadow-[0_3px_0_rgba(0,0,0,0.2)]">
          <div
            className={
              hideDock
                ? playDuelPath
                  ? "w-full py-1.5 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] sm:py-1.5 md:mx-auto md:max-w-xl md:px-3"
                  : "w-full py-1 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] sm:py-1.5 md:mx-auto md:max-w-xl md:px-3"
                : hubDesktopFullBleed
                  ? "mx-auto max-w-xl px-2 py-1 sm:px-3 sm:py-1.5 md:px-4"
                  : "mx-auto max-w-xl px-2 py-1 sm:px-3 sm:py-1.5"
            }
          >
            <div
              className="flex w-full min-w-0 items-center gap-1.5 overflow-visible sm:gap-2"
              data-no-ui-tap-sound
            >
              <div
                id="cwf-hud-coin-target"
                className={`min-w-0 flex-1 basis-0 overflow-visible ${hudCoinPop ? "animate-cwf-hud-coin-pop" : ""}`}
              >
                <CurrencyPill
                  icon={<CoinIcon />}
                  value={formatN(coins)}
                  plusHref="/store?tab=coins"
                  plusLabel="Buy coins"
                  onPlusWarmNavigate={() => void router.prefetch("/store?tab=coins")}
                />
              </div>
              <div
                id="cwf-hud-gem-target"
                className="min-w-0 flex-1 basis-0 overflow-visible"
              >
                <CurrencyPill
                  icon={<GemIcon />}
                  value={formatN(gems)}
                  plusHref="/store?tab=gems"
                  plusLabel="Buy gems"
                  onPlusWarmNavigate={() => void router.prefetch("/store?tab=gems")}
                />
              </div>
              <div
                id="cwf-hud-energy-target"
                className="min-w-0 flex-1 basis-0 overflow-visible"
              >
                <EnergyBlock
                  current={energyCur}
                  max={energyMax}
                  onPlusWarmNavigate={() => void router.prefetch("/store?tab=energy")}
                />
              </div>
              <SettingsButton onClick={() => setAudioSettingsOpen(true)} />
            </div>
          </div>
        </header>
      ) : null}

      <main
        className={
          hideDock
            ? hideTopHud
              ? "flex w-full max-w-none flex-1 flex-col overflow-x-hidden px-0 pb-4 pt-0 sm:pb-2"
              : playDuelPath
                ? "flex min-h-0 w-full max-w-none flex-1 flex-col overflow-hidden px-0 pb-0 pt-1.5 sm:pb-2 sm:pt-2"
                : matchmakingPath
                  ? "flex min-h-0 w-full max-w-none flex-1 flex-col overflow-hidden px-0 py-1 pb-0 sm:py-2"
                  : "flex w-full max-w-none flex-1 flex-col overflow-x-hidden px-0 py-1 pb-4 sm:py-2"
            : homeStaticShell
              ? "no-scrollbar mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col overflow-visible overscroll-none px-3 py-4 pb-28 sm:px-4 lg:max-w-6xl lg:px-6"
              : hubDesktopFullBleed
                ? "no-scrollbar mx-auto min-h-0 w-full max-w-xl flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 pb-28 sm:px-4 md:max-w-none md:px-0 md:py-0 md:pb-24"
                : "no-scrollbar mx-auto min-h-0 w-full max-w-xl flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 pb-28 sm:px-4 lg:max-w-6xl lg:px-6"
        }
      >
        {children ?? <Outlet />}
      </main>

      {!hideDock ? <BottomNav /> : null}

      {eventIconFlySpecs.length > 0 ? (
        <EventIconFlyLayer
          specs={eventIconFlySpecs}
          onAllFinished={handleEventIconFlyDone}
        />
      ) : null}
      {coinFlySpecs.length > 0 && typeof document !== "undefined"
        ? createPortal(
            <div
              className="pointer-events-none fixed inset-0 z-[130] overflow-visible"
              aria-hidden
            >
              <CoinFlyToHudLayer
                specs={coinFlySpecs}
                onAllFinished={handleCoinFlyDone}
              />
            </div>,
            document.body,
          )
        : null}

      {tierRewardGate ? (
        <EventTierRewardModal
          completions={tierRewardGate.flies.filter(
            (c) => c.completed && c.bonusCoins > 0,
          )}
          totalCoinsQueued={tierRewardGate.coins}
          onContinue={() => {
            const snapshot = tierRewardGate;
            setTierRewardGate(null);
            finishDeferredTierRewards(snapshot);
          }}
        />
      ) : null}

      <PurchaseCelebrationLayer />

      <AudioSettingsModal open={audioSettingsOpen} onClose={() => setAudioSettingsOpen(false)} />

      <NoEnergyModal />
    </div>
  );
}
